import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { ApiError } from "../../lib/api";
import { devicesApi, type DeviceDetail } from "../../lib/api/devices";
import { formatDateTime } from "../../lib/format";
import { t } from "../../lib/i18n";
import { invalidatePolicyViews } from "../../lib/policyQueries";
import { Alert } from "../ui/Alert";
import { Badge } from "../ui/Badge";
import { Card } from "../ui/Card";
import { ConfirmDialog } from "../ui/ConfirmDialog";
import { Switch } from "../ui/Switch";

export function DevicePolicyControl({ device, disabled = false }: { device: DeviceDetail; disabled?: boolean }) {
  const qc = useQueryClient();
  const [pending, setPending] = useState<boolean | null>(null);
  const supported = typeof device.policy_enabled === "boolean";
  const change = useMutation({
    mutationFn: async (enabled: boolean) => {
      const result = await devicesApi.setPolicyEnabled(device.id, enabled);
      // Do not claim success if an older server silently ignored this field.
      if (result.policy_enabled !== enabled) throw new Error(t("operations.policy.notSaved"));
      return result;
    },
    onSuccess: () => setPending(null),
    onSettled: () => invalidatePolicyViews(qc),
  });
  const error = change.error instanceof ApiError
    ? t(`errors.${change.error.code}`, change.error.message)
    : change.error?.message;

  return (
    <Card title={t("operations.policy.title")} description={t("operations.policy.description")}>
      <div className="stack">
        {!supported && <Alert tone="info">{t("operations.policy.unsupported")}</Alert>}
        <Switch
          checked={device.policy_enabled === true}
          disabled={!supported || disabled || device.status === "deprovisioned" || change.isPending}
          label={t("operations.policy.toggle")}
          sub={t("operations.policy.persistence")}
          onChange={(enabled) => { change.reset(); setPending(enabled); }}
        />
        <div className="row">
          <span>{t("operations.policy.reportedTunnel")}</span>
          <Badge tone={device.protection_active === true ? "success" : "neutral"}>
            {t(device.protection_active == null ? "operations.connectivity.unknown"
              : device.protection_active ? "common.active" : "common.inactive")}
          </Badge>
          <span className="muted">{formatDateTime(device.last_seen_at)}</span>
        </div>
        <p className="muted">{t("operations.policy.delivery")}</p>
        {error && <Alert tone="danger">{error}</Alert>}
      </div>
      <ConfirmDialog
        open={pending !== null}
        title={t(pending ? "operations.policy.enableTitle" : "operations.policy.disableTitle")}
        body={<div className="stack"><code>{device.device_uid}</code><p>{t("operations.policy.confirm")}</p></div>}
        confirmLabel={t("common.save")}
        tone={pending ? "primary" : "danger"}
        loading={change.isPending}
        onCancel={() => { if (!change.isPending) setPending(null); }}
        onConfirm={() => { if (pending !== null && !change.isPending) change.mutate(pending); }}
      />
    </Card>
  );
}
